import {
  useUserEmailMeById,
  useUserEmails,
  useUserEmailSendCode,
  useVerifyUserEmail,
} from '@starsoft/common/api';
import { useSearchParams } from '../../useSearchParams';
import { UseUserEmailsFormProps } from './props';
import {
  GenericError,
  PaginatedResponse,
  UserEmail,
} from '@starsoft/common/models';
import { Either } from '@starsoft/common/interfaces';
import { useCurrentModal, useModal } from '../../modals';
import { CommonModalsKey } from '@starsoft/common/enums';
import { useNextNavigate } from '../../useNextNavigate';
import { AxiosError } from 'axios';
import { Path, PathValue } from 'react-hook-form';
import { UserEmailVerifyInputDto } from '@starsoft/common/dto';
import { useNotificationService } from '../../useNotificationService';
import { useCallback, useEffect } from 'react';

/**
 * Custom hook to manage user email forms.
 *
 * @template T - The type of the form data.
 * @param {UseUserEmailsFormProps<T>} props - The properties for the hook.
 * @param {object} props.methods - Methods from react-hook-form.
 * @param {boolean} props.useMinimalForm - Flag to determine if minimal form should be used.
 * @param {boolean} [props.enabled=true] - Flag to enable or disable the hook.
 *
 * @returns {object} - Returns an object containing various states and functions for managing the form.
 */
export function useUserEmailsForm<T>({
  methods,
  useMinimalForm,
  enabled = true,
}: UseUserEmailsFormProps<T>) {
  // Get search parameters from the URL
  const search: URLSearchParams = useSearchParams();
  // Extract user email ID from search parameters
  const userEmailId: string | undefined = search.get('user-email-id');
  // Get the current modal state
  const modal: Either<string, undefined> = useCurrentModal(CommonModalsKey);
  // Hook for navigation
  const navigate = useNextNavigate();
  // Notification service for displaying messages
  const NotificationsService = useNotificationService();
  // Modal control functions
  const { onClose } = useModal(CommonModalsKey.UserEmail);

  // Destructure methods from react-hook-form
  const { watch, setValue } = methods;

  // Watch for changes in 'code' and 'emailId' fields
  const code = watch('code' as Path<T>);
  const emailId = watch('emailId' as Path<T>);
  // Determine if the submit button should be disabled
  const isSubmitDisabled: boolean =
    !userEmailId ||
    !code ||
    (typeof code === 'string' && code.length < 6) ||
    !emailId;

  // Hook to verify user email
  const {
    isPending: isPendingVerifyUserEmail,
    verifyUserEmail,
    verifyUserEmailAsync,
  } = useVerifyUserEmail();
  // Hook to send email verification code
  const useUserEmailSendCodeQuery = useUserEmailSendCode();
  const {
    sendCode,
    isPending: isPendingUserEmailSendCode,
    isSuccess: isSuccessUserEmailSendCode,
  } = useUserEmailSendCodeQuery;

  // Hook to fetch user email by ID
  const {
    userEmail,
    refetch,
    isPending: isPendingUserEmailById,
    isSuccess,
    error: errorUserEmailById,
  } = useUserEmailMeById({
    id: enabled ? (userEmailId ? Number(userEmailId) : undefined) : undefined,
  });

  /**
   * Callback function to handle successful fetching of user emails.
   *
   * @param {PaginatedResponse<UserEmail>} data - The paginated response containing user emails.
   */
  const externalOnSuccess = useCallback(
    (data: PaginatedResponse<UserEmail>) => {
      if (!useMinimalForm || !enabled) {
        return;
      }

      // Find the primary email from the response
      const email: UserEmail | undefined = data?.data?.find(
        email => email?.primary,
      );

      if (email && !userEmailId && enabled) {
        // Update search parameters and form values
        search.set('user-email-id', String(email.id));
        setValue('emailId' as Path<T>, email?.id as PathValue<T, Path<T>>);
        sendCode({
          emailId: email?.id?.toString(),
        });
        navigate({
          search: search.toString(),
          hash: modal,
        });
      }
    },
    [
      useMinimalForm,
      search,
      navigate,
      modal,
      sendCode,
      userEmailId,
      enabled,
      setValue,
    ],
  );

  // Hook to fetch user emails
  const {
    isLoading: isLoadingEmails,
    error: errorEmails,
    refetch: refetchEmails,
  } = useUserEmails({
    enabled,
    externalOnSuccess,
  });

  // Determine if there is an error
  const error: AxiosError<GenericError> | undefined =
    errorUserEmailById ?? errorEmails;
  // Determine if skeleton loading should be shown
  const isSkeleton: boolean = isPendingUserEmailById || false;

  /**
   * Function to handle errors by refetching data.
   */
  function handleError() {
    if (errorEmails) {
      refetchEmails();
    }
    if (errorUserEmailById) {
      refetch();
    }
  }

  /**
   * Function to display an error notification.
   */
  function onError() {
    NotificationsService.error({
      title: 'form_error_title',
      message: 'form_error_description',
    });
  }

  /**
   * Function to handle form submission.
   *
   * @param {UserEmailVerifyInputDto} data - The data to be submitted for email verification.
   */
  function onSubmit(data: UserEmailVerifyInputDto) {
    if (isSubmitDisabled) {
      onError();
      return;
    }

    verifyUserEmail(data);
  }

  /**
   * Function to handle fetching of user email.
   */
  function onFetchUserEmail() {
    if (!isSuccess || !userEmail || isSuccessUserEmailSendCode || !!emailId) {
      return;
    }

    if (userEmail.verified && !useMinimalForm) {
      onClose();
      return;
    }

    setValue('emailId' as Path<T>, userEmail.id as PathValue<T, Path<T>>);
  }

  // Effect to fetch user email when dependencies change
  useEffect(onFetchUserEmail, [
    emailId,
    onClose,
    setValue,
    sendCode,
    userEmail,
    isSuccess,
    isSuccessUserEmailSendCode,
    useMinimalForm,
  ]);

  // Return the state and functions for managing the form
  return {
    onSubmit,
    isSkeleton,
    error,
    isPendingVerifyUserEmail,
    isPendingUserEmailSendCode,
    isPendingUserEmailById,
    handleError,
    onError,
    useUserEmailSendCodeQuery,
    isLoadingEmails,
    userEmailId,
    userEmail,
    isSubmitDisabled,
    submission: {
      verifyUserEmail,
      verifyUserEmailAsync,
      isLoading: isPendingVerifyUserEmail,
    },
  };
}
